import { useEffect } from 'react';
import { supabase } from '../../supabase';
import { DEACTIVATION_POLL_MS, DEACTIVATION_RESUME_MIN_GAP_MS } from '../../config/sessionPolicy';
import { setRevokedFlag } from '../../utils/auth/sessionLiveness';
import { subscribeAppResume } from '../../utils/ui/appResumeGate';
import useAssuranceSettled from './useAssuranceSettled';

/**
 * Periodic deactivation / role-drift poll — extracted verbatim from
 * AuthContext.js (500-line cap) with ONE behavior addition (P1 access-status
 * addendum, 2026-09-24): the first check now also runs IMMEDIATELY when a
 * session's tenant first resolves (cold start / resume with an existing
 * token), not only after the first `DEACTIVATION_POLL_MS` interval.
 *
 * Why the addition: `resolveTenantMembershipForUser`'s JWT fast path can
 * hand back a STALE `{ role, tenantId }` for a member or company deactivated
 * AFTER the current access token was minted — the member-deactivation toggle
 * only kills the refresh-token chain, and (until a separate lane closes it)
 * the platform tenant-deactivation action revokes nothing at all. This poll
 * is the only backstop for that staleness, and because it previously only started
 * ticking on an interval, the app shell could render with every query denied
 * by RLS for up to the poll interval before the first check
 * caught it. Running `check` once synchronously with the effect — in
 * addition to, not instead of, the unchanged interval — closes that window:
 * it forces a full `getTenantMembershipForUser` DB re-fetch (bypassing the
 * stale JWT claims) via `syncSessionRef.current(session, {
 * forceTenantMembershipFetch: true })`, and syncSession.js's
 * `get_my_access_status` hookup then picks the right access-status screen
 * from that correctly-resolved (empty) membership.
 *
 * `check` itself, and the interval cadence it runs on, are otherwise
 * unchanged from the inline version this replaces.
 */
export function useDeactivationPoll({
  sessionUserId,
  tenantId,
  loading,
  tenantLoading,
  role,
  offlineSinceRef,
  syncSessionRef,
}) {
  // Nothing but the 2FA decision runs before the 2FA code (KAN-676 intent): no
  // check_my_access_status until assurance settles. The server stays the boundary.
  const { settled: assuranceSettled } = useAssuranceSettled(sessionUserId ?? null);
  useEffect(() => {
    if (!sessionUserId || !tenantId || loading || tenantLoading || !assuranceSettled) return;
    let active = true;
    let lastCheckAt = 0;
    let inFlight = false;
    const check = async () => {
      if (!navigator.onLine || offlineSinceRef.current || inFlight) return;
      inFlight = true;
      lastCheckAt = Date.now();
      try {
        const { data } = await supabase.rpc('check_my_access_status', { target_tenant_id: tenantId });
        if (!active) return;
        const row = Array.isArray(data) ? data[0] : data;
        // Trigger a full resync on:
        //   - membership/tenant deactivation (existing behavior)
        //   - role drift (RD-AUTH-008: cached JWT role doesn't match live role —
        //     happens when a supervisor is demoted to worker mid-session, or vice
        //     versa). Server still rejects unauthorized mutations, but the UI
        //     keeps supervisor chrome until we refresh.
        const roleDrifted = row?.role && role && row.role !== role;
        if (!row || row.membership_active === false || row.tenant_active === false || roleDrifted) {
          const { data: { session } } = await supabase.auth.getSession();
          if (active && syncSessionRef.current) {
            // Flag admin-initiated deactivation so the SIGNED_OUT handler
            // (and login page) can show "access revoked" instead of "session expired".
            // setRevokedFlag() timestamps it — shares the same freshness-checked
            // format the liveness-probe path uses (see sessionLiveness.js).
            if (row?.membership_active === false) {
              setRevokedFlag();
            }
            syncSessionRef.current(session, { forceTenantMembershipFetch: true });
          }
        }
      } catch { /* network error — preserve current state */ } finally { inFlight = false; }
    };
    void check(); // P1 addendum, 2026-09-24: immediate first check (was interval-only)
    // KAN-1044: 15 min, and never while the tab is hidden (a background tab made 12 calls/h for
    // nobody). Coming back runs ONE check if the last is >= 60s old; realtime membership /
    // session-revocation watches + the live-session gate stay the immediate revocation paths.
    const tick = () => { if (document.visibilityState !== 'hidden') void check(); };
    const id = setInterval(tick, DEACTIVATION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckAt >= DEACTIVATION_RESUME_MIN_GAP_MS) void check();
    };
    const onBack = () => { if (Date.now() - lastCheckAt >= DEACTIVATION_RESUME_MIN_GAP_MS) void check(); };
    // Also the shared gate (focus / pageshow returns after 60s away) and the network coming back.
    const onOnline = () => { void check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    const stopResume = subscribeAppResume(onBack);
    return () => {
      active = false; clearInterval(id); stopResume();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [sessionUserId, tenantId, loading, tenantLoading, assuranceSettled, role, offlineSinceRef, syncSessionRef]);
}
